import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import groupBy from 'lodash/groupBy';
import isNil from 'lodash/isNil';
import startCase from 'lodash/startCase';
import * as API from 'api';
import { AccordionSection, ButtonExportCSVAsync, ButtonSwitch, CardContent, CardIcon, CardLabel, CardValue, DateRangePicker, Empty, H4, HeaderCell, HeaderRow, SectionHeader, Table, TableBody, TableHead, TooltipIcon } from 'components';
import { appConfig } from 'config/app';
import { theme } from 'design';
import ListContext, { FetchDataQueryType, ListContextProvider } from 'providers/ListContextProvider';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getAcceptanceLedgerRequest, getPrefundLedgerError, Ledger, selectLedgerEntries } from 'store/Collections';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { selectSiteAcceptance } from 'store/Site';
import { IStore } from 'store/types';
import { ApiQueryOperator, IAcceptanceLedgerEntry, IApiList, IApiQueryParam, ILedgerEntry, LedgerEntryType } from 'types';
import { getLastElement, sumBy } from 'utils/arrays';
import { CSVFormatter, IExportCSV } from 'utils/csvformatter';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateForApi } from 'utils/date';
import { routeConstants } from '../../routes';
import { ActiveReportTab, BRCard, BRMultiTableLayout, BRSection, BRSummaryRow, BRTableHeader, BRTableSection, DailyReportRow, DetailCreditReportRow, DetailDebitReportRow, IParams } from './components';

const ReportsBankReconciliation = () => {
  const dispatch = useDispatch();

  const DEFAULT_END_DATE = dayjs(new Date()).format('MM-DD-YYYY');
  const DEFAULT_START_DATE = dayjs(new Date()).subtract(6, 'day').format('MM-DD-YYYY');
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentSiteAcceptance = useSelector(selectSiteAcceptance());
  const { option = ActiveReportTab.Prefunded } = useParams<IParams>();
  const history = useHistory();

  const acceptanceReduxLedger = useSelector(selectLedgerEntries());
  const [ledger, setLedger] = useState<IApiList<ILedgerEntry | IAcceptanceLedgerEntry> | null>(null);
  const ledgerEntries = ledger?.data;
  const ledgerTotals = ledger?.total;

  const acceptanceChecked = option === ActiveReportTab.Acceptance;

  const onSwitchChange = useCallback(() => {
    history.push(`${routeConstants.private.reportsBankReconciliation}/${acceptanceChecked ? ActiveReportTab.Prefunded : ActiveReportTab.Acceptance}`);
  }, [acceptanceChecked]);

  useEffect(() => {
    if (acceptanceChecked && (!currentSite?.id || (currentSiteAcceptance && !currentSiteAcceptance.enabled))) {
      history.push(routeConstants.private.reportsBankReconciliation);
    }
  }, [history, acceptanceChecked, currentSite, currentSiteAcceptance]);

  const fetchData = (queryParams: FetchDataQueryType) => {
    const startDate = (queryParams.q || []).find((q) => q.key === 'payment_date' && q.operator === ApiQueryOperator.gte)?.value;
    const endDate = (queryParams.q || []).find((q) => q.key === 'payment_date' && q.operator === ApiQueryOperator.lte)?.value;

    const dependencies = queryParams.meta as {
      currentOrganizationIdDep: string;
      currentSiteIdDep: string;
    };

    const currentSiteIdDep = dependencies?.currentSiteIdDep ? dependencies?.currentSiteIdDep : currentSite?.id;

    if (currentSiteIdDep && startDate && endDate) {
      if (!acceptanceChecked) {
        setIsLoadingPrefund(true);
        API.Sites.listPrefundLedgerEntries(currentSiteIdDep, startDate as string, endDate as string, { limit: 0 })
          .then((response) => setLedger(response.body))
          .catch((error) => dispatch(getPrefundLedgerError(error)))
          .finally(() => setIsLoadingPrefund(false));
      } else {
        dispatch(getAcceptanceLedgerRequest(currentSiteIdDep, startDate as string, endDate as string, { limit: 0 }));
        setLedger(acceptanceReduxLedger);
      }
    }
  };

  const [isLoadingPrefund, setIsLoadingPrefund] = useState(false);
  const isLoadingAcceptance = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Ledger.GET_ACCEPTANCE_LEDGER_REQUEST));
  const isLoading = isLoadingPrefund || isLoadingAcceptance;
  // entries for detail section
  const credits = (ledgerEntries || []).filter((entry) => [LedgerEntryType.CREDIT].includes(entry.entryType));
  const debits = (ledgerEntries || []).filter((entry) => [LedgerEntryType.DEBIT].includes(entry.entryType));

  // calculations for Summary
  const { startingBalance, totalCredits, totalDebits, endingBalance } = (() => {
    let startBalance = ledgerEntries?.[0]?.balance;
    const firstEntry = ledgerEntries?.[0];

    // need to credit or debit the amount from the first entry to get the true start balance
    if (startBalance && firstEntry) {
      if (firstEntry.entryType === LedgerEntryType.CREDIT) {
        startBalance -= firstEntry.amount;
      }

      if (firstEntry.entryType === LedgerEntryType.DEBIT) {
        startBalance += firstEntry.amount;
      }
    }

    return {
      startingBalance: startBalance,
      totalCredits: ledgerEntries && sumBy(credits, 'amount'),
      totalDebits: ledgerEntries && sumBy(debits, 'amount'),
      endingBalance: ledgerEntries && ledgerEntries?.[0] && getLastElement(ledgerEntries)?.balance,
    };
  })();

  // calculations for Daily Reports section
  const { dailyCredits, dailyDebits } = (() => {
    const creditLedgerEntriesByDay = groupBy(credits, (entry) => dayjs(entry.createdAt).format('MM-DD-YYYY'));
    const debitLedgerEntriesByDay = groupBy(debits, (entry) => dayjs(entry.createdAt).format('MM-DD-YYYY'));
    const creditsByDay = Object.values(creditLedgerEntriesByDay).map((entries: (ILedgerEntry | IAcceptanceLedgerEntry)[]) => ({
      date: dayjs(entries[0].createdAt).format('MM-DD-YYYY'),
      amount: sumBy(entries, 'amount'),
    }));
    const debitsByDay = Object.values(debitLedgerEntriesByDay).map((entries: (ILedgerEntry | IAcceptanceLedgerEntry)[]) => ({
      date: dayjs(entries[0].createdAt).format('MM-DD-YYYY'),
      amount: sumBy(entries, 'amount'),
    }));

    return {
      dailyCredits: ledgerEntries && creditsByDay,
      dailyDebits: ledgerEntries && debitsByDay,
    };
  })();

  const getEmployeeName = (data: ILedgerEntry | IAcceptanceLedgerEntry) => {
    if ('employee' in data) {
      return data.employee.name;
    }
    if ('employeeName' in data) {
      return data.employeeName;
    }
    return '';
  };

  const exportSummaryCSV = async (startingBalance: number, endingBalance: number, totalCredits: number, totalDebits: number): Promise<IExportCSV> => CSVFormatter.bankRecSummary(startingBalance, endingBalance, totalCredits, totalDebits);
  const exportDailyCSV = async (credits: { date: string; amount: number }[], debits: { date: string; amount: number }[]): Promise<IExportCSV> => CSVFormatter.bankRecDaily(credits, debits);
  const exportDetailsCSV = async (credits: (ILedgerEntry | IAcceptanceLedgerEntry)[], debits: (ILedgerEntry | IAcceptanceLedgerEntry)[]): Promise<IExportCSV> => CSVFormatter.bankRecDetails(credits, debits);

  return (
    <>
      {currentSite && (
        <ListContextProvider
          fetchData={fetchData}
          totalSelector={() => ledgerTotals}
          defaultOffset={0}
          defaultLimit={0}
          defaultSort="created_at"
          dependencies={{
            currentSiteIdDep: currentSite.id,
            acceptanceChecked,
          }}
          defaultFilters={[
            {
              key: 'payment_date',
              operator: ApiQueryOperator.gte,
              value: formatDateForApi(DEFAULT_START_DATE),
            },
            {
              key: 'payment_date',
              operator: ApiQueryOperator.lte,
              value: formatDateForApi(DEFAULT_END_DATE),
            },
          ]}
          dataKey="reportsLedger"
        >
          <ListContext.Consumer>
            {({ query, methods }) => {
              const { onFiltersChange } = methods;
              const { q } = query;

              const setDates = ([start, end]: string[]) => {
                const isValid = dayjs(start, 'YYYY-MM-DD', true).isValid() && dayjs(end, 'YYYY-MM-DD', true).isValid();
                if (isValid) {
                  (() => {
                    const filters = [
                      {
                        key: 'payment_date',
                        operator: ApiQueryOperator.gte,
                        value: start,
                      },
                      {
                        key: 'payment_date',
                        operator: ApiQueryOperator.lte,
                        value: end,
                      },
                    ];

                    // Clear ledger
                    setLedger(null);
                    onFiltersChange(filters, true);
                  })();
                }
              };
              const startDate = (q || []).find((q: IApiQueryParam) => q.key === 'payment_date' && q.operator === ApiQueryOperator.gte)?.value;
              const endDate = (q || []).find((q: IApiQueryParam) => q.key === 'payment_date' && q.operator === ApiQueryOperator.lte)?.value;

              const hasSummaryData = !isNil(startingBalance) && !isNil(endingBalance) && !isNil(totalCredits) && !isNil(totalDebits);
              const hasDailyReportData = !!dailyCredits && !!dailyDebits && (dailyCredits.length >= 1 || dailyDebits.length >= 1);
              const hasDetailsReportsData = !!credits && !!debits && (credits.length >= 1 || debits.length >= 1);

              return (
                <>
                  <BRSection>
                    <SectionHeader title="Bank Reconciliation" subtitle="Choose a date range to run report">
                      <DateRangePicker
                        fluidWidth={false}
                        inputHeight="small"
                        onSubmit={setDates}
                        pickerPosition="right"
                        startDate={DEFAULT_START_DATE}
                        endDate={DEFAULT_END_DATE}
                        submitLabel="apply"
                        maxDays={appConfig.DATE_RANGE.MAX_DAYS_3_MONTHS}
                      />
                      {currentSiteAcceptance?.enabled && <ButtonSwitch onChange={onSwitchChange} checked={acceptanceChecked} leftText="Prefunded" rightText="Tip Acceptance" data-cy="acceptance-switch" />}
                    </SectionHeader>
                  </BRSection>

                  <AccordionSection
                    dataCy="summary-section"
                    actions={
                      <ButtonExportCSVAsync
                        data-cy="summary-export-btn"
                        disabled={!hasSummaryData}
                        exportFunc={() => exportSummaryCSV(startingBalance, endingBalance, totalCredits, totalDebits)}
                        fileName={`BankSummary_${currentSite.name}_${acceptanceChecked ? `${ActiveReportTab.Acceptance}_` : ''}${startDate}_${endDate}`}
                      />
                    }
                    hideContentOverflow
                    title="Summary"
                  >
                    <BRSummaryRow>
                      <BRCard>
                        <CardIcon bgColor={theme.color.neutral[100]} icon="Wallet" fill={theme.color.neutral[400]} />
                        <CardContent data-cy="summary-starting-balance-card">
                          <CardLabel isLoading={isLoading}>Starting Balance</CardLabel>
                          <CardValue isLoading={isLoading}>{!isNil(startingBalance) ? centsToDollarsDisplay(startingBalance) : '--'}</CardValue>
                        </CardContent>
                      </BRCard>

                      <BRCard>
                        <CardIcon bgColor={theme.color.success.light} icon="Payment" fill={theme.color.success.medium} />
                        <CardContent data-cy="summary-credits-card">
                          <CardLabel isLoading={isLoading}>Credits</CardLabel>
                          <CardValue isLoading={isLoading}>{!isNil(totalCredits) ? centsToDollarsDisplay(totalCredits as number) : '--'}</CardValue>
                        </CardContent>
                      </BRCard>

                      <BRCard>
                        <CardIcon bgColor={theme.color.warning.light} icon="Payment" fill={theme.color.warning.medium} />
                        <CardContent data-cy="summary-debits-card">
                          <CardLabel isLoading={isLoading}>Debits</CardLabel>
                          <CardValue isLoading={isLoading}>{!isNil(totalDebits) ? `(${centsToDollarsDisplay(totalDebits as number)})` : '--'}</CardValue>
                        </CardContent>
                      </BRCard>

                      <BRCard>
                        <CardIcon bgColor={theme.color.danger.light} icon="Payment" fill={theme.color.danger.medium} />
                        <CardContent data-cy="summary-ending-balance-card">
                          <CardLabel isLoading={isLoading}>Ending Balance</CardLabel>
                          <CardValue isLoading={isLoading}>{!isNil(endingBalance) ? centsToDollarsDisplay(endingBalance as number) : '--'}</CardValue>
                        </CardContent>
                      </BRCard>
                    </BRSummaryRow>
                  </AccordionSection>

                  <AccordionSection
                    dataCy="daily-section"
                    actions={
                      <ButtonExportCSVAsync
                        data-cy="daily-reports-export-csv-btn"
                        disabled={!hasDailyReportData}
                        exportFunc={() => exportDailyCSV(dailyCredits, dailyDebits)}
                        fileName={`BankDaily_${currentSite.name}_${acceptanceChecked ? `${ActiveReportTab.Acceptance}_` : ''}${startDate}_${endDate}`}
                      />
                    }
                    hideContentOverflow
                    title="Daily Reports"
                  >
                    <BRMultiTableLayout>
                      <BRTableSection>
                        <BRTableHeader>
                          <H4 data-cy="daily-credits-header">
                            <strong>Daily Credits</strong>
                            {isLoading || isNil(dailyCredits) ? '' : ` (${dailyCredits.length})`}
                          </H4>
                          <TooltipIcon id="daily-credits-header-tooltip" iconSize={13.25} iconMarginLeft={3.75}>
                            These are credits to your Kickfin account.
                          </TooltipIcon>
                        </BRTableHeader>

                        {dailyCredits && dailyCredits.length >= 1 ? (
                          <Table>
                            <TableHead>
                              <HeaderRow>
                                <HeaderCell>{isLoading ? '' : 'Date'}</HeaderCell>

                                <HeaderCell align="right">{isLoading ? '' : 'Amount'}</HeaderCell>
                              </HeaderRow>
                            </TableHead>

                            <TableBody>
                              {dailyCredits.map((datum: { date: string; amount: number }, index: number) => (
                                <DailyReportRow key={`daily-credits-${index}`} data-cy="daily-credits-report-row" data-value={datum.date} amount={centsToDollarsDisplay(datum.amount)} date={datum.date} isLoading={isLoading} />
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          dailyCredits && <Empty container containerBorder containerHeight={66} message="No credits found for this date range." />
                        )}
                      </BRTableSection>

                      <BRTableSection>
                        <BRTableHeader>
                          <H4 data-cy="daily-debits-header">
                            <strong>Daily Debits</strong>
                            {isLoading || isNil(dailyDebits) ? '' : ` (${dailyDebits.length})`}
                          </H4>
                          <TooltipIcon id="daily-debits-header-tooltip" iconSize={13.25} iconMarginLeft={3.75}>
                            These are debits to your Kickfin account.
                          </TooltipIcon>
                        </BRTableHeader>

                        {dailyDebits && dailyDebits.length >= 1 ? (
                          <Table>
                            <TableHead>
                              <HeaderRow>
                                <HeaderCell>{isLoading ? 'Date' : ''}</HeaderCell>

                                <HeaderCell align="right">{isLoading ? '' : 'Amount'}</HeaderCell>
                              </HeaderRow>
                            </TableHead>

                            <TableBody>
                              {dailyDebits.map((datum: { date: string; amount: number }, index: number) => (
                                <DailyReportRow data-cy="daily-debits-report-row" data-value={datum.date} key={index} amount={centsToDollarsDisplay(datum.amount)} date={datum.date} isLoading={isLoading} />
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          dailyDebits && <Empty container containerBorder containerHeight={66} message="No debits found for this date range." />
                        )}
                      </BRTableSection>
                    </BRMultiTableLayout>
                  </AccordionSection>

                  <AccordionSection
                    dataCy="detailed-section"
                    actions={
                      credits ? (
                        <ButtonExportCSVAsync
                          data-cy="detailed-report-export-btn"
                          disabled={!hasDetailsReportsData}
                          exportFunc={() => exportDetailsCSV(credits, debits)}
                          fileName={`BankDetails_${currentSite.name}_${acceptanceChecked ? `${ActiveReportTab.Acceptance}_` : ''}${startDate}_${endDate}`}
                        />
                      ) : null
                    }
                    hideContentOverflow
                    title="Detailed Reports"
                  >
                    <BRMultiTableLayout>
                      <BRTableSection>
                        <BRTableHeader>
                          <H4 data-cy="detailed-credits-header">
                            <strong>Credit Details</strong>
                            {!isNil(credits) && isLoading ? '' : ` (${credits.length})`}
                          </H4>
                          <TooltipIcon id="detailed-credits-header-tooltip" iconSize={13.25} iconMarginLeft={3.75}>
                            These are credits to your Kickfin account.
                          </TooltipIcon>
                        </BRTableHeader>

                        {credits && credits.length >= 1 ? (
                          <Table>
                            <TableHead>
                              <HeaderRow>
                                <HeaderCell>{isLoading ? '' : 'Date'}</HeaderCell>

                                <HeaderCell>{isLoading ? '' : 'Type'}</HeaderCell>

                                <HeaderCell>{isLoading ? '' : 'Description'}</HeaderCell>

                                <HeaderCell align="right">{isLoading ? '' : 'Amount'}</HeaderCell>
                              </HeaderRow>
                            </TableHead>

                            <TableBody>
                              {credits.map((datum: ILedgerEntry | IAcceptanceLedgerEntry) => (
                                <DetailCreditReportRow
                                  key={datum.id}
                                  trxId={datum.id}
                                  data-cy="detailed-credits-report-row"
                                  data-value={dayjs(datum.createdAt).format('MM-DD-YYYY')}
                                  amount={centsToDollarsDisplay(datum.amount)}
                                  date={dayjs(datum.createdAt).format('MM-DD-YYYY')}
                                  type={startCase(datum.entryType)}
                                  description={datum.description}
                                  notes={datum.txnRefNotes}
                                />
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          !isLoading && credits?.length === 0 && <Empty container containerBorder containerHeight={66} message="No credits found for this date range." />
                        )}
                      </BRTableSection>

                      <BRTableSection>
                        <BRTableHeader>
                          <H4 data-cy="detailed-debits-header">
                            <strong>Debit Details</strong>
                            {isLoading ? '' : ` (${debits.length})`}
                          </H4>
                          <TooltipIcon id="detailed-debits-header-tooltip" iconSize={13.25} iconMarginLeft={3.75}>
                            These are debits to your Kickfin account.
                          </TooltipIcon>
                        </BRTableHeader>

                        {isLoading && Array(5).map((e, i) => <DetailDebitReportRow key={i} isLoading />)}
                        {debits.length >= 1 ? (
                          <Table>
                            <TableHead>
                              <HeaderRow>
                                <HeaderCell>{isLoading ? '' : 'Date'}</HeaderCell>

                                <HeaderCell>{isLoading ? '' : 'Type'}</HeaderCell>

                                <HeaderCell>{isLoading ? '' : 'Description'}</HeaderCell>

                                <HeaderCell>{isLoading ? '' : 'Employee'}</HeaderCell>

                                <HeaderCell>{isLoading ? '' : 'Transaction ID'}</HeaderCell>

                                <HeaderCell align="right">{isLoading ? '' : 'Amount'}</HeaderCell>
                              </HeaderRow>
                            </TableHead>

                            <TableBody>
                              {debits.map((datum: ILedgerEntry | IAcceptanceLedgerEntry, index: number) => (
                                <DetailDebitReportRow
                                  key={index}
                                  data-cy="detailed-debits-report-row"
                                  data-value={dayjs(datum.createdAt).format('MM-DD-YYYY')}
                                  amount={centsToDollarsDisplay(datum.amount)}
                                  date={dayjs(datum.createdAt).format('MM-DD-YYYY')}
                                  description={datum.description}
                                  employeeName={getEmployeeName(datum)}
                                  transactionId={datum.txnRefId}
                                  isLoading={isLoading}
                                  type={startCase(datum.entryType)}
                                />
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          !isLoading && debits?.length === 0 && <Empty container containerBorder containerHeight={66} message="No debits found for this date range" />
                        )}
                      </BRTableSection>
                    </BRMultiTableLayout>
                  </AccordionSection>
                </>
              );
            }}
          </ListContext.Consumer>
        </ListContextProvider>
      )}
    </>
  );
};

export default ReportsBankReconciliation;
